import { ReactNode, useState } from "react";

import { Icon } from "@/lib/components/ui/Icon/Icon";
import { iconList } from "@/lib/helpers/iconList";
import { cn } from "@/lib/utils";

import styles from "./MenuButton.module.scss";

export interface ButtonProps {
  id?: string;
  dataTest?: string;
  onClick?: () => void;
  label?: string;
  secondaryLabel?: string;
  trailingContent?: ReactNode;
  iconName: keyof typeof iconList;
  type: "add" | "open";
  isSelected?: boolean;
  color: "gold" | "primary" | "secondary";
  parentHovered?: boolean;
  expandable?: boolean;
  expanded?: boolean;
}

export const MenuButton = (props: ButtonProps): JSX.Element => {
  const {
    id,
    dataTest,
    color,
    iconName,
    isSelected,
    label,
    onClick,
    parentHovered,
    expandable,
    expanded,
    secondaryLabel,
    trailingContent,
  } = props;
  const [isHovered, setIsHovered] = useState(false);
  const isPrimaryTitle = color === "primary" && (isHovered || parentHovered);

  const wrapperClassName = cn(styles.menu_button_wrapper, {
    [styles.selected]: Boolean(isSelected),
    [styles.gold]: color === "gold",
    [styles.secondary]: color === "secondary",
    [styles.icon_only]: !label,
  });

  const titleClassName = cn(styles.title, {
    [styles.gold]: color === "gold",
    [styles.secondary]: color === "secondary",
    [styles.primary]: isPrimaryTitle,
  });

  return (
    <div
      id={id}
      data-test={dataTest}
      className={wrapperClassName}
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      <div className={styles.left}>
        <Icon name={iconName} size="normal" color={color} />
        {label && <span className={titleClassName}>{label}</span>}
      </div>
      {expandable && (
        <Icon
          name={expanded ? "chevronUp" : "chevronDown"}
          size="small"
          color={color}
        />
      )}
      {secondaryLabel && (
        <span className={styles.secondaryLabel}>{secondaryLabel}</span>
      )}
      {trailingContent && (
        <div className={styles.trailingContent}>{trailingContent}</div>
      )}
    </div>
  );
};
